import React, { Fragment } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { graphql } from 'gatsby';
import {
  Badge,
  Button,
  Icon,
  List,
  ListItem,
  Reveal,
} from 'chaoskit/src/components';

import BaseLayout from '../layouts/BaseLayout';
import Hero from '../partials/Hero';
import CustomerLogos from '../partials/global/CustomerLogos';
import { Data } from '../components';
import logoBlack from '../assets/media/logo-black.svg';

const Edition = ({ edition }) => {
  const { label, labelSub, cta, included } = edition;

  return (
    <div className="column-6@medium">
      <div className="pricing__edition">
        <div className="pricing__edition-section">
          <div className="pricing__edition-header">
            <img src={logoBlack} alt="Gremlin" />
            <Badge className={`badge--pricing-${label}`} label={label} />
          </div>
          <h5 className="u-textMuted u-textCenter u-mt--regular u-mb--medium">
            <Data>{labelSub}</Data>
          </h5>
          <div className="pricing__edition-priceBlock">
            <Button
              type="teal"
              url={cta.url}
              target={cta.newWindow ? '_blank' : null}
              rel={cta.newWindow ? 'noopener noreferrer' : null}
            >
              <Data>{cta.label}</Data>
            </Button>
          </div>
        </div>
        <div className="pricing__edition-section">
          <h5 className="u-textCenter u-textMuted u-mb--large">
            What&apos;s Included
          </h5>
          <List className="pricing__edition-includedList">
            {included.map(include => {
              const listItemClasses = cx({
                'has-toggle': include.dropdownItems,
              });
              const dropdownListClasses = cx('pricing__dropdown-list', {
                'has-divide':
                  include.dropdownItems && include.dropdownItems.length > 3,
              });

              const listItemRender = (
                <Fragment>
                  <Data>{include.item}</Data>
                  {include.itemMeta && (
                    <span className="u-ml--small u-textMuted u-textSmall u-textNormal">
                      <Data>{include.itemMeta}</Data>
                    </span>
                  )}
                </Fragment>
              );

              return (
                <ListItem key={include.item} className={listItemClasses}>
                  {include.dropdownItems ? (
                    <Reveal
                      reveal
                      trigger={{
                        label: (
                          <Fragment>
                            {listItemRender}
                            <Icon
                              className="u-ml--small"
                              icon="caret-down"
                              size="small"
                            />
                          </Fragment>
                        ),
                        props: {
                          type: 'reset',
                          className: 'pricing__dropdown-trigger',
                        },
                      }}
                    >
                      <List className={dropdownListClasses}>
                        {include.dropdownItems.map(item => (
                          <ListItem key={item.item}>{item.item}</ListItem>
                        ))}
                      </List>
                    </Reveal>
                  ) : (
                    listItemRender
                  )}
                </ListItem>
              );
            })}
          </List>
        </div>
      </div>
    </div>
  );
};

Edition.propTypes = {
  edition: PropTypes.object.isRequired,
};

const Pricing = props => {
  const {
    data: {
      file: { childDataYaml: pageData },
    },
  } = props;

  return (
    <BaseLayout>
      <Helmet
        title={pageData.title}
        meta={[{ name: 'description', content: pageData.description }]}
      />
      <Hero
        particles={false}
        className="u-gradient--teal-darkBlue pricing__hero"
      >
        <div className="hero-content">
          <div className="container">
            <div className="hero-titleWrapper">
              <h1 className="hero-title">
                <Data>{pageData.hero.heading}</Data>
              </h1>
              <div className="hero-titleSub">
                <Data markdown>{pageData.hero.blurb}</Data>
              </div>
            </div>
          </div>
        </div>
      </Hero>
      <section className="section u-bgPanel">
        <div className="container">
          <div className="pricing__editions-wrapper">
            <div className="row row--deepMatch">
              {pageData.editions.map(edition => (
                <Edition key={edition.label} edition={edition} />
              ))}
            </div>
          </div>
          <CustomerLogos className="u-pv--remove" />
        </div>
      </section>
      <section className="section">
        <div className="container">
          <div className="row row--largeGutter row--xlargeGutter@medium">
            <div className="column-5@medium u-textCenter u-textLeft@medium">
              <h3 className="u-mb--large">
                <Data>{pageData.faq.heading}</Data>
              </h3>
              <div className="u-textLarge u-textHeading u-textTeal u-mb--regular">
                <Data markdown>{pageData.faq.blurb}</Data>
              </div>
              <Button type="teal" url={pageData.faq.cta.url}>
                <Data>{pageData.faq.cta.label}</Data>
              </Button>
            </div>
            <div className="column-7@medium">
              <List className="pricing__faq-list">
                {pageData.faq.items.map(item => (
                  <ListItem key={item.question}>
                    <div className="u-textLarge u-textHeading u-textBold">
                      <Data>{item.question}</Data>
                    </div>
                    <div className="u-mt--small">
                      <Data markdown>{item.answer}</Data>
                    </div>
                  </ListItem>
                ))}
              </List>
            </div>
          </div>
        </div>
      </section>
    </BaseLayout>
  );
};

Pricing.propTypes = {
  data: PropTypes.object.isRequired,
};

export default Pricing;

export const pageQuery = graphql`
  query PricingPageData {
    file(name: { eq: "pricing" }) {
      childDataYaml {
        title
        description
        hero {
          blurb
          heading
        }
        editions {
          label
          labelSub
          cta {
            label
            url
            newWindow
          }
          included {
            item
            itemMeta
            dropdownItems {
              item
            }
          }
        }
        faq {
          heading
          blurb
          cta {
            label
            url
          }
          items {
            question
            answer
          }
        }
      }
    }
  }
`;
